import {
  addDays,
  invoiceBalanceCents,
  leadReceivedCents,
  type FinanceContract,
  type FinanceInvoice,
  type FinanceReceipt,
  type RevenueGoal,
} from "./contracts";
import type { Lead } from "../../lib/types";

/**
 * Fórmulas financeiras, separadas da apresentação (AGENTS §5, §10).
 *
 * Toda função aqui é pura e determinística e declara, no próprio nome e na
 * documentação, a unidade, o período e o denominador — os três itens que a
 * interface precisa mostrar ao lado do número para ele ser auditável.
 *
 * Nenhuma delas "prevê" nada por modelo: a projeção é a soma do que já está
 * lançado e ainda em aberto. Chamar isso de previsão de IA seria inventar
 * capacidade (§3).
 */

export interface FinancePeriod {
  /** Primeiro dia do período, inclusive. */
  from: string;
  /** Último dia do período, inclusive. */
  to: string;
}

export interface RevenuePoint {
  /** Dia civil do ponto. */
  date: string;
  cents: number;
}

export interface ChannelRevenue {
  channel: string;
  cents: number;
  /** Quantos recebimentos compõem o valor. */
  receipts: number;
}

function inPeriod(date: string, period: FinancePeriod): boolean {
  return date >= period.from && date <= period.to;
}

export function receiptsInPeriod(
  receipts: readonly FinanceReceipt[],
  period: FinancePeriod,
): FinanceReceipt[] {
  return receipts.filter((receipt) => inPeriod(receipt.receivedOn, period));
}

/** Receita do período: soma dos recebimentos com data dentro da janela. */
export function revenueCents(
  receipts: readonly FinanceReceipt[],
  period: FinancePeriod,
): number {
  return receiptsInPeriod(receipts, period).reduce(
    (total, receipt) => total + receipt.amountCents,
    0,
  );
}

/** Série diária de receita, com dias sem recebimento valendo zero. */
export function revenueSeries(
  receipts: readonly FinanceReceipt[],
  period: FinancePeriod,
): RevenuePoint[] {
  const byDay = new Map<string, number>();
  for (const receipt of receiptsInPeriod(receipts, period)) {
    byDay.set(receipt.receivedOn, (byDay.get(receipt.receivedOn) ?? 0) + receipt.amountCents);
  }

  const points: RevenuePoint[] = [];
  for (let date = period.from; date <= period.to; date = addDays(date, 1)) {
    points.push({ date, cents: byDay.get(date) ?? 0 });
    if (points.length > 1100) break;
  }
  return points;
}

/**
 * Receita por canal de origem do lead.
 *
 * Usa a origem gravada no recebimento, não a origem atual do lead: mudar a
 * origem de um lead hoje não pode reescrever de onde veio o dinheiro de ontem
 * (§10, métricas históricas não mudam por edição de cadastro).
 */
export function revenueByChannel(
  receipts: readonly FinanceReceipt[],
  period: FinancePeriod,
): ChannelRevenue[] {
  const byChannel = new Map<string, ChannelRevenue>();
  for (const receipt of receiptsInPeriod(receipts, period)) {
    const channel = receipt.sourceSnapshot?.trim() || "Sem origem";
    const current = byChannel.get(channel) ?? { channel, cents: 0, receipts: 0 };
    current.cents += receipt.amountCents;
    current.receipts += 1;
    byChannel.set(channel, current);
  }
  return [...byChannel.values()].sort((left, right) => right.cents - left.cents);
}

/** Vendas fechadas no período: recebimentos marcados como primeira venda. */
export function salesClosed(
  receipts: readonly FinanceReceipt[],
  period: FinancePeriod,
): FinanceReceipt[] {
  return receiptsInPeriod(receipts, period).filter((receipt) => receipt.firstSale);
}

/**
 * Projeção dos próximos `days` dias.
 *
 * Denominador: faturas já lançadas, não canceladas, com vencimento entre amanhã
 * e o fim da janela, contadas pelo SALDO em aberto. Não extrapola tendência.
 */
export function projectedCents(
  invoices: readonly FinanceInvoice[],
  receipts: readonly FinanceReceipt[],
  today: string,
  days: number,
): number {
  const limit = addDays(today, days);
  return invoices
    .filter(
      (invoice) =>
        !invoice.cancelledAt && invoice.dueOn > today && invoice.dueOn <= limit,
    )
    .reduce((total, invoice) => total + invoiceBalanceCents(invoice, receipts), 0);
}

/**
 * A mesma projeção, dia a dia: o saldo em aberto de cada vencimento entre
 * amanhã e o fim da janela, com dias sem vencimento valendo zero. A soma da
 * série é exatamente `projectedCents` — o gráfico e o número não podem contar
 * histórias diferentes (§10).
 */
export function projectionSeries(
  invoices: readonly FinanceInvoice[],
  receipts: readonly FinanceReceipt[],
  today: string,
  days: number,
): RevenuePoint[] {
  const limit = addDays(today, days);
  const byDay = new Map<string, number>();
  for (const invoice of invoices) {
    if (invoice.cancelledAt || invoice.dueOn <= today || invoice.dueOn > limit) continue;
    const balance = invoiceBalanceCents(invoice, receipts);
    if (balance > 0) byDay.set(invoice.dueOn, (byDay.get(invoice.dueOn) ?? 0) + balance);
  }
  const points: RevenuePoint[] = [];
  for (let date = addDays(today, 1); date <= limit; date = addDays(date, 1)) {
    points.push({ date, cents: byDay.get(date) ?? 0 });
    if (points.length > 1100) break;
  }
  return points;
}

/** Faturas vencidas e ainda com saldo, da mais antiga para a mais nova. */
export function overdueInvoices(
  invoices: readonly FinanceInvoice[],
  receipts: readonly FinanceReceipt[],
  today: string,
): FinanceInvoice[] {
  return invoices
    .filter(
      (invoice) =>
        !invoice.cancelledAt &&
        invoice.dueOn < today &&
        invoiceBalanceCents(invoice, receipts) > 0,
    )
    .sort((left, right) => left.dueOn.localeCompare(right.dueOn));
}

export interface ClientValue {
  leadId: string;
  cents: number;
}

/** Valor recebido por cliente, do maior para o menor. Unidade: centavos. */
export function clientValues(
  leads: readonly Lead[],
  receipts: readonly FinanceReceipt[],
): ClientValue[] {
  return leads
    .filter((lead) => Boolean(lead.clientSince) && !lead.archived)
    .map((lead) => ({ leadId: lead.id, cents: leadReceivedCents(receipts, lead.id) }))
    .sort((left, right) => right.cents - left.cents);
}

export interface ValueSummary {
  clients: number;
  totalCents: number;
  meanCents: number;
  medianCents: number;
}

/**
 * Resumo do valor por cliente.
 *
 * Média e mediana são calculadas separadamente de propósito: com poucos
 * clientes e um contrato grande, só a mediana descreve o caso típico (§10).
 */
export function clientValueSummary(values: readonly ClientValue[]): ValueSummary {
  const amounts = values.map((value) => value.cents).sort((left, right) => left - right);
  const total = amounts.reduce((sum, value) => sum + value, 0);
  if (amounts.length === 0) {
    return { clients: 0, totalCents: 0, meanCents: 0, medianCents: 0 };
  }
  const middle = Math.floor(amounts.length / 2);
  const median =
    amounts.length % 2 === 0
      ? Math.round((amounts[middle - 1] + amounts[middle]) / 2)
      : amounts[middle];
  return {
    clients: amounts.length,
    totalCents: total,
    meanCents: Math.round(total / amounts.length),
    medianCents: median,
  };
}

export interface GoalProgress {
  goal?: RevenueGoal;
  achievedCents: number;
  /** Fração entre 0 e 1; `null` quando não há meta definida para o período. */
  ratio: number | null;
}

/** Progresso contra a meta cujo período contém `today`. */
export function goalProgress(
  goals: readonly RevenueGoal[],
  receipts: readonly FinanceReceipt[],
  today: string,
): GoalProgress {
  const goal = goals.find((item) => today >= item.periodStart && today <= item.periodEnd);
  if (!goal) return { goal: undefined, achievedCents: 0, ratio: null };
  const achieved = revenueCents(receipts, { from: goal.periodStart, to: goal.periodEnd });
  return {
    goal,
    achievedCents: achieved,
    ratio: goal.amountCents > 0 ? achieved / goal.amountCents : null,
  };
}

/** Contratos vigentes em `today`: começaram, não terminaram nem foram encerrados. */
export function activeContracts(
  contracts: readonly FinanceContract[],
  today: string,
): FinanceContract[] {
  return contracts.filter(
    (contract) =>
      contract.startOn <= today &&
      (!contract.endOn || contract.endOn >= today) &&
      (!contract.cancelledOn || contract.cancelledOn >= today),
  );
}

/** Receita recorrente mensal: soma dos contratos mensais vigentes. */
export function recurringMonthlyCents(
  contracts: readonly FinanceContract[],
  today: string,
): number {
  return activeContracts(contracts, today)
    .filter((contract) => contract.kind === "monthly")
    .reduce((total, contract) => total + contract.amountCents, 0);
}
